/**
 * The three tiers of support actions, rendered from `actionsByTier` /
 * `TIER_META` (Task 8) with NO per-action `if`. Matches the mockup's tier
 * blocks (`fleet-device-support.html:149-198`) structurally and verbatim on
 * copy.
 *
 * Two things the registry deliberately does NOT decide, per the rulings in
 * this task's brief:
 *
 *  - PHASE GATING lives here, not in the registry (`actionsByTier` ignores
 *    `availableInPhase` on purpose). Tier 2/3 actions render VISIBLY
 *    DISABLED rather than hidden — the tiering is the point of the design
 *    and has to be visible from day one, even before phase 2/3 ship.
 *  - LOCK/UNLOCK is one toggle tile, not two buttons. The registry excludes
 *    `unlock` entirely (see `actionRegistry.ts`'s docblock) — this component
 *    drives the single "Lock till" row's label/icon/action id from the
 *    device's `terminalControl.status`, which is NOT on `FleetRow` today (it
 *    is written by the separate `PosControlTab` WS-lock flow, keyed off the
 *    register, not off a `device_report` result) — so it is read
 *    defensively off `row.device` as an untyped optional and treated as
 *    "unlocked" whenever it is absent, matching the copy's own "Reversible
 *    from here" framing.
 */
import { useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Lock, Unlock } from 'lucide-react';
import type { FleetActionId, FleetTier } from '@platform/shared-types';
import {
  actionsByTier, DISABLED_REINSTALL_OS, TIER_META, CURRENT_PHASE, type UiAction,
} from './actionRegistry';
import type { FleetRow } from './fleetRows';
import TierTwoConfirm from './TierTwoConfirm';

/** Read defensively — `terminalControl` is not on `FleetRow`/`FleetDeviceRecord` yet. */
function isLocked(row: FleetRow): boolean {
  const tc = (row.device as unknown as { terminalControl?: { status?: string } } | undefined)?.terminalControl;
  return tc?.status === 'locked';
}

const TIER_TAG_COLOR: Record<FleetTier, string> = {
  1: 'text-emerald-600',
  2: 'text-amber-600',
  3: 'text-rose-600',
};

function ActionButton({
  icon: Icon, label, help, danger, disabled, title, busy, onClick,
}: {
  icon: LucideIcon; label: string; help: string; danger?: boolean; disabled?: boolean;
  title?: string; busy?: boolean; onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      title={title}
      className={`flex min-h-[56px] items-start gap-2.5 rounded-lg border px-2.5 py-2.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
        danger
          ? 'border-rose-200 bg-white hover:border-rose-300'
          : 'border-gray-200 bg-white hover:border-gray-400'
      }`}
    >
      <Icon size={16} className={`mt-0.5 shrink-0 ${danger ? 'text-rose-600' : 'text-gray-500'}`} />
      <span className="min-w-0">
        <b className={`block text-[12.5px] font-semibold ${danger ? 'text-rose-600' : 'text-gray-900'}`}>
          {busy ? 'Sending…' : label}
        </b>
        <span className="mt-0.5 block text-[11px] leading-[1.4] text-gray-500">{help}</span>
      </span>
    </button>
  );
}

export default function ActionTiers({
  row, busyAction, onRun, onRequestDestructive,
}: {
  row: FleetRow;
  /** The action id currently being issued, so its button can say "Sending…". */
  busyAction?: FleetActionId | null;
  /** Tier 1: fires immediately. Tier 2: fires once `TierTwoConfirm` confirms, carrying its params. */
  onRun: (action: FleetActionId, params?: Record<string, unknown>) => void;
  /** Tier 3: opens `DestructiveConfirm` at the drawer level — it needs the live impact probe and code issuance, which live above this component. */
  onRequestDestructive?: (action: FleetActionId) => void;
}) {
  const locked = isLocked(row);
  const [pendingTierTwo, setPendingTierTwo] = useState<FleetActionId | null>(null);

  return (
    <div className="space-y-3">
      {([1, 2, 3] as FleetTier[]).map((tier) => {
        const meta = TIER_META[tier];
        const actions: UiAction[] = actionsByTier(tier);
        const phaseOpen = tier <= CURRENT_PHASE;

        return (
          <div key={tier} className={`overflow-hidden rounded-xl border ${tier === 3 ? 'border-rose-300' : 'border-gray-200'}`}>
            <div className={`flex flex-wrap items-center gap-2 px-3 py-2 text-[12px] font-bold ${meta.pill}`}>
              <span className={`rounded bg-white px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide ${TIER_TAG_COLOR[tier]}`}>
                {meta.label}
              </span>
              <span>{meta.title}</span>
              <span className="ml-auto text-right text-[11px] font-normal">{meta.why}</span>
            </div>
            <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2">
              {actions.map((a) => {
                // The lock/unlock toggle: `a.id === 'lock'` is the only
                // registry row that switches identity based on live state.
                const isLockRow = a.id === 'lock';
                const effectiveId: FleetActionId = isLockRow && locked ? 'unlock' : a.id;
                const label = isLockRow && locked ? 'Unlock till' : a.label;
                const help = isLockRow && locked
                  ? 'Clear the freeze and return control to the cashier'
                  : a.help;
                const icon = isLockRow && locked ? Unlock : (isLockRow ? Lock : a.icon);

                /**
                 * "Update now" against a frozen till can only ever produce the
                 * server's 409 `auto_update_off` — the same reasoning as
                 * `deliveryPending`: a live button whose only outcome is a
                 * failed command is worse than no button. The title says what
                 * to do about it, so the operator is not left guessing.
                 */
                const frozen = a.id === 'update_now' && !row.autoUpdate;
                const disabled = !phaseOpen || busyAction === effectiveId || !!a.deliveryPending || frozen;
                const title = !phaseOpen
                  ? `Tier ${tier} is not yet built — phase ${tier} ships later`
                  : a.deliveryPending
                    ? 'Not deployed yet — delivery for this action ships in a later task'
                    : frozen
                      ? 'Auto-update is off for this till — turn it back on first'
                      : undefined;

                const handleClick = () => {
                  if (tier === 1) { onRun(effectiveId); return; }
                  if (tier === 2) { setPendingTierTwo(effectiveId); return; }
                  // Tier 3 — always goes through DestructiveConfirm, never runs directly.
                  onRequestDestructive?.(effectiveId);
                };

                return (
                  <ActionButton
                    key={a.id}
                    icon={icon}
                    label={label}
                    help={help}
                    danger={tier === 3}
                    disabled={disabled}
                    busy={busyAction === effectiveId}
                    title={title}
                    onClick={handleClick}
                  />
                );
              })}
              {/* The mockup's permanently-disabled "Reinstall OS" row — has no
                  FLEET_ACTIONS id, can never run remotely, always rendered
                  and always disabled. Dropping it loses a design statement. */}
              {tier === 3 && (
                <ActionButton
                  icon={DISABLED_REINSTALL_OS.icon}
                  label={DISABLED_REINSTALL_OS.label}
                  help={DISABLED_REINSTALL_OS.help}
                  danger
                  disabled
                  title={DISABLED_REINSTALL_OS.help}
                />
              )}
            </div>
          </div>
        );
      })}

      {pendingTierTwo && (
        <TierTwoConfirm
          row={row}
          action={pendingTierTwo}
          busy={busyAction === pendingTierTwo}
          onCancel={() => setPendingTierTwo(null)}
          onConfirm={(params) => {
            onRun(pendingTierTwo, params);
            setPendingTierTwo(null);
          }}
        />
      )}
    </div>
  );
}
